'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { IngredientWithDetails } from '@/lib/data/recipes';
import { Icon, type IconName } from '@/components/ui/icon';
import { DIET_ICON } from '@/components/ui/dietary-indicators';
import { formatQuantity } from '@/lib/utils/placeholders';
import { getIngredientThumbImageUrl } from '@/lib/utils/image-url';
import { ThumbImage } from '@/components/ui/thumb-image';
import { convertForDisplay, type UnitSystem } from '@/lib/utils/unit-display';
import { unitLabel } from '@/lib/utils/unit-label';
import { isTightUnit } from '@/lib/utils/format-amount';
import { prepMethodSegments } from '@/lib/utils/prep-method';
import { toGrams } from '@/lib/utils/compute-nutrition';
import styles from './ingredient-list.module.css';

type DietaryKey = 'vegan' | 'vegetarian' | 'glutenFree' | 'dairyFree' | 'hashimoto';

interface DietaryCheck {
  // Slug stored in profile (e.g. 'gluten-free') for filter matching.
  slug: string;
  // i18n key under `dietary.*` and `ingredients.violations.*`.
  i18nKey: DietaryKey;
  safeFlag: keyof Pick<IngredientWithDetails, 'isVegan' | 'isVegetarian' | 'isGlutenFree' | 'isDairyFree' | 'isHashimotoSafe'>;
  noteField?: 'hashimotoNote';
  icon: IconName;
}

// Icons sourced from the shared DIET_ICON map (dietary-indicators) so cards,
// hero and this violation panel speak one visual vocabulary — vegan = sprout,
// vegetarian = leaf, etc. Hashimoto stays 'warn': in this panel it flags an
// unsafe ingredient, so the warning glyph reads truer than a diet mark.
const DIETARY_CHECKS: DietaryCheck[] = [
  { slug: 'vegan', i18nKey: 'vegan', safeFlag: 'isVegan', icon: DIET_ICON.vegan },
  { slug: 'vegetarian', i18nKey: 'vegetarian', safeFlag: 'isVegetarian', icon: DIET_ICON.vegetarian },
  { slug: 'gluten-free', i18nKey: 'glutenFree', safeFlag: 'isGlutenFree', icon: DIET_ICON.glutenFree },
  { slug: 'dairy-free', i18nKey: 'dairyFree', safeFlag: 'isDairyFree', icon: DIET_ICON.dairyFree },
  { slug: 'hashimoto', i18nKey: 'hashimoto', safeFlag: 'isHashimotoSafe', noteField: 'hashimotoNote', icon: 'warn' },
];

interface IngredientListProps {
  ingredients: IngredientWithDetails[];
  selectedServings: number;
  baseServings: number;
  /** Slugs of dietary preferences the viewer has active (e.g. ['vegan', 'hashimoto']). */
  activeDietaryPreferences?: string[];
  locale?: 'en' | 'de';
  unitSystem?: UnitSystem;
  /** Controlled — when true, each row shows its per-line kcal chip. The toggle
   *  lives on the section-title row (owned by the parent), so this is a prop. */
  showKcal?: boolean;
}

/** Kcal for the amount displayed on a row (displayQuantity scaled to servings),
 *  or null when the ingredient has no per-100g data or an unresolvable unit. */
function displayedKcal(ing: IngredientWithDetails, scaleFactor: number): number | null {
  if (ing.caloriesPer100g == null || ing.displayQuantity == null) return null;
  const amount = ing.displayQuantity * scaleFactor;
  const grams = toGrams(amount, ing.displayUnitAbbrev, ing.densityGPerMl, ing.gPerPiece);
  if (grams == null) return null;
  return (ing.caloriesPer100g * grams) / 100;
}

/** True when at least one ingredient can show a per-line kcal figure — drives
 *  whether the parent renders the "Show calories" toggle. */
export function hasDisplayableKcal(
  ingredients: IngredientWithDetails[],
  selectedServings: number,
  baseServings: number,
): boolean {
  const scaleFactor = selectedServings / Math.max(1, baseServings);
  return ingredients.some((i) => displayedKcal(i, scaleFactor) != null);
}

export function IngredientList({
  ingredients,
  selectedServings,
  baseServings,
  activeDietaryPreferences = [],
  locale = 'en',
  unitSystem = 'metric',
  showKcal = false,
}: IngredientListProps) {
  const t = useTranslations();
  const safeBase = Math.max(1, baseServings);
  const scaleFactor = selectedServings / safeBase;
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const [openDietaryWarnings, setOpenDietaryWarnings] = useState<Set<string>>(new Set());

  function toggle(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  }

  if (ingredients.length === 0) {
    return <p className={styles.empty}>{t('ingredients.empty')}</p>;
  }

  return (
    <>
      <ul className={styles.list} aria-label={t('ingredients.title')}>
      {ingredients.map((ing, idx) => {
        // Only use displayQuantity + displayUnit for cooking quantities.
        // Do NOT fall back to shoppingQuantity — it may be in different units
        // (e.g., "1 bag" → numeric scale gives "3.5 bag" which is nonsensical).
        const scaledRaw = ing.displayQuantity == null ? null : ing.displayQuantity * scaleFactor;
        const converted = convertForDisplay(scaledRaw, ing.displayUnitAbbrev, unitSystem);
        const scaledQty = converted.qty == null ? '' : formatQuantity(converted.qty, locale, converted.unit);
        const unitAbbrev = unitLabel(converted.unit, locale);
        const violations = DIETARY_CHECKS.filter(
          (check) => activeDietaryPreferences.includes(check.slug) && !ing[check.safeFlag],
        );
        const hasViolations = violations.length > 0;
        const hasNotes = Boolean(ing.purchaseTips || ing.substitutionNotes);
        const hasThumbnail =
          ing.ingredientImageUrl != null && ing.ingredientImageStatus === 'generated';
        const key = String(ing.id);
        const notesOpen = openNotes.has(key);
        const dietaryWarningOpen = openDietaryWarnings.has(key);

        return (
          <li
            key={ing.id}
            className={styles.item}
            data-dietary-warning={hasViolations ? 'true' : undefined}
            style={{ '--stagger-index': Math.min(idx, 8) } as React.CSSProperties}
          >
            <div className={styles.row}>
              <div className={styles.thumbnail} aria-hidden="true">
                {hasThumbnail ? (
                  <ThumbImage
                    thumbSrc={getIngredientThumbImageUrl(ing.ingredientImageUrl)!}
                    fallbackSrc={ing.ingredientImageUrl!}
                    alt=""
                    width={40}
                    height={40}
                    className={styles.thumbnailImage}
                  />
                ) : (
                  <Icon
                    name="fork-knife"
                    size={18}
                    strokeWidth={1.25}
                    className={styles.thumbnailFallbackIcon}
                  />
                )}
              </div>

              <div className={styles.details}>
                <div className={styles.quantityUnit} data-tight={isTightUnit(unitAbbrev) ? 'true' : undefined}>
                  {scaledQty && <span className={styles.amount}>{scaledQty}</span>}
                  {unitAbbrev && <span className={styles.unit}>{unitAbbrev}</span>}
                </div>
                <div className={styles.namePrep}>
                  <span className={styles.name}>{ing.name}</span>
                  {ing.preparationMethod && (() => {
                    const segments = prepMethodSegments(ing.preparationMethod);
                    const rendered = segments
                      .map((seg) => {
                        if (seg.type === 'key') return t(`recipes.prepMethods.${seg.value}`);
                        if (seg.type === 'sep') {
                          if (seg.value === 'comma') return ', ';
                          if (seg.value === 'or') return ` ${t('recipes.prepJoiners.or')} `;
                          return ` ${t('recipes.prepJoiners.and')} `;
                        }
                        return seg.value;
                      })
                      .join('');
                    return <span className={styles.prepMethod}>{rendered}</span>;
                  })()}
                </div>
              </div>

              <div className={styles.actions}>
                {showKcal && (() => {
                  const kcal = displayedKcal(ing, scaleFactor);
                  if (kcal == null) return null;
                  return (
                    <span
                      className={styles.kcalChip}
                      aria-label={t('ingredients.kcalForDisplayedAmountAria', { count: Math.round(kcal) })}
                    >
                      {t('recipes.kcal', { count: Math.round(kcal) })}
                    </span>
                  );
                })()}
                {hasNotes && (
                  <button
                    type="button"
                    className={styles.notesBtn}
                    onClick={() => setOpenNotes((s: Set<string>) => toggle(s, key))}
                    aria-expanded={notesOpen}
                    aria-label={t(notesOpen ? 'ingredients.hideNotes' : 'ingredients.showNotes', { name: ing.name })}
                  >
                    <Icon
                      name="chevron-down"
                      size={12}
                      strokeWidth={2}
                      className={`${styles.notesChevron}${notesOpen ? ` ${styles.notesChevronOpen}` : ''}`}
                    />
                  </button>
                )}
                {hasViolations && (
                  <button
                    type="button"
                    className={styles.dietaryWarningBtn}
                    onClick={() => setOpenDietaryWarnings((s: Set<string>) => toggle(s, key))}
                    aria-expanded={dietaryWarningOpen}
                    aria-label={t(dietaryWarningOpen ? 'ingredients.hideWarnings' : 'ingredients.showWarnings', { name: ing.name })}
                  >
                    {violations.map(({ icon, slug }) => (
                      <Icon key={slug} name={icon} size={14} strokeWidth={1.5} />
                    ))}
                  </button>
                )}
              </div>
            </div>

            {hasViolations && dietaryWarningOpen && (
              <div className={styles.dietaryWarningPanel} role="note">
                {violations.map(({ icon, slug, i18nKey, noteField }) => (
                  <div key={slug} className={styles.dietaryWarningRow}>
                    <Icon name={icon} size={13} strokeWidth={1.5} className={styles.dietaryWarningIcon} />
                    <p className={styles.dietaryWarningText}>
                      <strong>{t(`dietary.${i18nKey}`)}:</strong>{' '}
                      {noteField && ing[noteField] ? ing[noteField] : t(`ingredients.violations.${i18nKey}`)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {hasNotes && notesOpen && (
              <div className={styles.notesContent}>
                {ing.purchaseTips && (
                  <p className={styles.noteText}>
                    {/* TODO #87: ideal icon would be shopping-bag */}
                    <Icon
                      name="cart"
                      size={14}
                      strokeWidth={1.75}
                      className={styles.noteIcon}
                    />
                    <span>
                      <strong>{t('ingredients.whereToFind')}:</strong> {ing.purchaseTips}
                    </span>
                  </p>
                )}
                {ing.substitutionNotes && (
                  <p className={styles.noteText}>
                    {/* TODO #87: ideal icon would be arrow-left-right */}
                    <Icon
                      name="sort"
                      size={14}
                      strokeWidth={1.75}
                      className={styles.noteIcon}
                    />
                    <span>
                      <strong>{t('ingredients.substitutions')}:</strong> {ing.substitutionNotes}
                    </span>
                  </p>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
    </>
  );
}
